/**
 * 出荷チェック (エアロジ突合) ページ。2026-10-08 新設。
 *
 * 「突合を実行」ボタン 1 つで:
 *   1. airlogi-bridge (Chrome 拡張) からエアロジの現況を取得
 *      (未引当一覧 / 引当履歴 / 在庫 / 入荷予定)
 *   2. バックエンド /api/shipping-check/reconcile に渡して全注文を突合
 *   3. 🔴 異常 / 🟡 入荷待ち / 本日のフロー進捗 を表示
 */
import { useState } from "react";
import {
  reconcileShippingCheck,
  type ReconcileAlertEntry,
  type ReconcileResult,
} from "@/api/endpoints";
import { ApiError } from "@/api/client";
import {
  callBridge,
  type AllocationHistoryRow,
  type IncomingStockRow,
  type SessionStatus,
  type StockInfoRow,
  type UnallocatedOrderRow,
} from "@/utils/airlogiBridge";

type Phase =
  | { step: "idle" }
  | { step: "running"; detail: string }
  | { step: "done"; result: ReconcileResult }
  | { step: "error"; message: string };

const HISTORY_DAYS_BACK = 3; // 引当履歴の取得範囲 (当日含め過去 N 日)

function ymdDaysAgo(days: number): string {
  const d = new Date(Date.now() + 9 * 3600 * 1000);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10).replace(/-/g, "");
}

const ALERT_SECTIONS: {
  key: keyof ReconcileResult["alerts"];
  title: string;
  hint: string;
}[] = [
  {
    key: "missing_in_airlogi",
    title: "CSV 出力済みなのにエアロジに存在しない",
    hint: "アップロード漏れ・空ファイル・商品マスタ未登録の可能性。該当店舗の CSV を再出力してアップロードしてください。",
  },
  {
    key: "allocation_problem",
    title: "エアロジ側ステータスが異常 (引当エラー・保留など)",
    hint: "エアロジ画面で該当注文の状態を確認してください。",
  },
  {
    key: "stock_available_unallocated",
    title: "在庫があるのに引当されない",
    hint: "エアロジの商品マスタと JAN の不一致などの可能性。エアロジ画面で確認してください。",
  },
  {
    key: "ghost_in_airlogi",
    title: "システムでは終了済みなのにエアロジに出荷指示が残っている",
    hint: "誤出荷・在庫拘束の原因になります。エアロジ側で出荷指示の削除を検討してください。",
  },
  {
    key: "packing_pending_old",
    title: "引当が前日以前なのに未梱包",
    hint: "倉庫での梱包漏れの可能性。エアロジ/倉庫に確認してください。",
  },
];

function AlertTable({ entries }: { entries: ReconcileAlertEntry[] }) {
  return (
    <table className="w-full text-xs border">
      <thead className="bg-gray-50">
        <tr>
          <th className="border px-2 py-1 text-left">注文番号</th>
          <th className="border px-2 py-1 text-left">店舗</th>
          <th className="border px-2 py-1 text-left">状態</th>
          <th className="border px-2 py-1 text-left">経過</th>
          <th className="border px-2 py-1 text-left">詳細</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e, i) => (
          <tr key={`${e.order_no}-${i}`}>
            <td className="border px-2 py-1 font-mono">{e.order_no}</td>
            <td className="border px-2 py-1">
              {e.platform ?? "?"}
              {e.country_code ? ` ${e.country_code.toUpperCase()}` : ""}
            </td>
            <td className="border px-2 py-1">
              {e.packing_status
                ? `梱包:${e.packing_status} / 出荷:${e.shipping_status ?? "?"}` +
                  (e.system_status ? ` (システム:${e.system_status})` : "")
                : (e.airlogi_status ?? e.system_status ?? e.status ?? "")}
            </td>
            <td className="border px-2 py-1">
              {e.days_since_export != null && `出力から ${e.days_since_export} 日`}
              {e.days_waiting != null && `${e.days_waiting} 日待ち`}
              {e.allocated_at && `引当 ${e.allocated_at}`}
            </td>
            <td className="border px-2 py-1">
              {e.jans?.map((j) => (
                <span key={j.jan} className="mr-2 whitespace-nowrap">
                  {j.jan} (必要 {j.required ?? 1} / 在庫 {j.stock ?? 0}
                  {j.incoming_date ? ` / 入荷予定 ${j.incoming_date}` : ""})
                </span>
              ))}
              {e.note}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ShippingCheckPage() {
  const [phase, setPhase] = useState<Phase>({ step: "idle" });

  const run = async () => {
    try {
      setPhase({ step: "running", detail: "拡張との接続を確認中…" });
      const session = await callBridge<SessionStatus>("airlogi.getSessionStatus");
      if (!session.ok) {
        setPhase({ step: "error", message: session.message });
        return;
      }
      if (!session.data.loggedIn) {
        setPhase({
          step: "error",
          message:
            "Air-Logi にログインしていません。別タブで Air-Logi にログインしてから再実行してください。",
        });
        return;
      }

      setPhase({ step: "running", detail: "エアロジの未引当一覧を取得中…" });
      const unalloc = await callBridge<{ rows: UnallocatedOrderRow[] }>(
        "airlogi.getUnallocatedOrders",
      );
      if (!unalloc.ok) {
        setPhase({ step: "error", message: `未引当一覧の取得に失敗: ${unalloc.message}` });
        return;
      }

      setPhase({ step: "running", detail: "エアロジの引当履歴を取得中…" });
      const history = await callBridge<{ rows: AllocationHistoryRow[] }>(
        "airlogi.getAllocationHistory",
        { fromYmd: ymdDaysAgo(HISTORY_DAYS_BACK - 1), toYmd: ymdDaysAgo(0) },
      );
      if (!history.ok) {
        setPhase({ step: "error", message: `引当履歴の取得に失敗: ${history.message}` });
        return;
      }

      setPhase({ step: "running", detail: "エアロジの在庫情報を取得中… (1 分ほどかかることがあります)" });
      const stock = await callBridge<{ rows: StockInfoRow[] }>(
        "airlogi.getStockInfo",
      );

      setPhase({ step: "running", detail: "エアロジの入荷予定を取得中…" });
      const incoming = await callBridge<{ rows: IncomingStockRow[] }>(
        "airlogi.getIncomingStocks",
      );

      setPhase({ step: "running", detail: "システムの注文と突合中…" });
      const result = await reconcileShippingCheck({
        unallocated: unalloc.data.rows,
        allocation_history: history.data.rows,
        stock: stock.ok ? stock.data.rows : undefined,
        incoming: incoming.ok ? incoming.data.rows : undefined,
      });
      setPhase({ step: "done", result });
    } catch (e) {
      setPhase({
        step: "error",
        message: e instanceof ApiError ? e.message : String(e),
      });
    }
  };

  const result = phase.step === "done" ? phase.result : null;

  return (
    <div className="space-y-6">
      <div className="card space-y-4">
        <h2 className="text-lg font-semibold">出荷チェック (エアロジ突合)</h2>
        <p className="text-sm text-gray-500">
          システムの全注文とエアロジの現況 (未引当・引当履歴・在庫・入荷予定) を突合し、
          取り込み漏れ・引当エラー・梱包漏れ・入荷待ちを分類します。
          Air-Logi に Chrome でログインした状態で実行してください。
        </p>
        <button
          className="btn-accent"
          onClick={run}
          disabled={phase.step === "running"}
        >
          {phase.step === "running" ? "実行中…" : "突合を実行"}
        </button>
        {phase.step === "running" && (
          <div className="bg-blue-50 border border-blue-200 text-blue-900 p-3 rounded text-sm">
            {phase.detail}
          </div>
        )}
        {phase.step === "error" && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm">
            {phase.message}
          </div>
        )}
      </div>

      {result && (
        <>
          <div
            className={`card ${result.alert_total === 0 ? "border-green-300" : "border-red-300"}`}
          >
            {result.alert_total === 0 ? (
              <p className="text-green-700 font-semibold">
                ✅ 異常はありません (突合時刻: {new Date(result.as_of).toLocaleString()})
              </p>
            ) : (
              <p className="text-red-700 font-semibold">
                🔴 要確認 {result.alert_total} 件 (突合時刻: {new Date(result.as_of).toLocaleString()})
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              エアロジ: 未引当 {result.inputs.unallocated_rows} 件 / 引当履歴 {result.inputs.history_rows} 件
              {result.inputs.has_stock_data
                ? ` / 在庫 ${result.inputs.stock_rows} 品目`
                : " / 在庫データなし (在庫判定スキップ)"}
              {" "}/ 本日引当分の未梱包 {result.info.packing_pending_today} 件 (当日分は正常)
            </p>
          </div>

          {ALERT_SECTIONS.map(({ key, title, hint }) => {
            const entries = result.alerts[key];
            if (!entries.length) return null;
            return (
              <div key={key} className="card space-y-2">
                <h3 className="font-semibold text-red-700">
                  🔴 {title} ({entries.length} 件)
                </h3>
                <p className="text-xs text-gray-500">{hint}</p>
                <AlertTable entries={entries} />
              </div>
            );
          })}

          {result.waiting.stock_wait.length > 0 && (
            <div className="card space-y-2">
              <h3 className="font-semibold text-amber-700">
                🟡 入荷待ち (在庫 0・無在庫販売の正常な待ち) ({result.waiting.stock_wait.length} 件)
              </h3>
              <p className="text-xs text-gray-500">
                在庫が入荷してエアロジが引き当てれば自動的に通常フローへ合流します。
                入荷予定が無い商品は発注の要否をご確認ください。
              </p>
              <AlertTable entries={result.waiting.stock_wait} />
            </div>
          )}

          {result.waiting.waiting_unknown.length > 0 && (
            <div className="card space-y-2">
              <h3 className="font-semibold text-gray-600">
                ⚪ 引当待ち (分類保留) ({result.waiting.waiting_unknown.length} 件)
              </h3>
              <AlertTable entries={result.waiting.waiting_unknown} />
            </div>
          )}

          <div className="card space-y-2">
            <h3 className="font-semibold">本日のフロー進捗 (プラットフォーム別)</h3>
            <table className="w-full text-xs border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border px-2 py-1 text-left">店舗</th>
                  <th className="border px-2 py-1">本日取込</th>
                  <th className="border px-2 py-1">ラベル発行済</th>
                  <th className="border px-2 py-1">未発行</th>
                  <th className="border px-2 py-1">エラー</th>
                  <th className="border px-2 py-1">CSV 出力済</th>
                  <th className="border px-2 py-1">引当済</th>
                  <th className="border px-2 py-1">印刷済</th>
                  <th className="border px-2 py-1">発送完了</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(result.flow_today).map(([platform, c]) => (
                  <tr key={platform}>
                    <td className="border px-2 py-1 font-semibold">{platform}</td>
                    <td className="border px-2 py-1 text-center">{c.fetched}</td>
                    <td className="border px-2 py-1 text-center">{c.issued}</td>
                    <td className={`border px-2 py-1 text-center ${c.issue_pending ? "bg-amber-50 text-amber-800 font-semibold" : ""}`}>
                      {c.issue_pending}
                    </td>
                    <td className={`border px-2 py-1 text-center ${c.error ? "bg-red-50 text-red-700 font-semibold" : ""}`}>
                      {c.error}
                    </td>
                    <td className="border px-2 py-1 text-center">{c.exported}</td>
                    <td className="border px-2 py-1 text-center">{c.allocated}</td>
                    <td className="border px-2 py-1 text-center">{c.printed}</td>
                    <td className="border px-2 py-1 text-center">{c.shipped}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-gray-500">
              「引当済」はエアロジの引当履歴 (直近 {HISTORY_DAYS_BACK} 日) と照合した件数です。
              午前の突合では引当前のため 0 でも異常ではありません。
            </p>
          </div>
        </>
      )}
    </div>
  );
}
